/**
 * Scatter Overlay
 * Renders an explicit set of (x, y) markers on top of the WebGL canvas,
 * aligned to the chart's coordinate system. Used by MultiSeriesWebGLChart
 * to draw anomaly markers over a base line series.
 *
 * Coordinate math mirrors PointsOverlay/useWebGLInstance EXACTLY so markers
 * stay locked to the line under pan/zoom.
 */
import React, { useMemo } from 'react';
import { ZoomState } from '../types';
import { CHART_PADDING } from '../constants';

export interface ScatterPoint {
  /** Timestamp in seconds (same scale as line series). */
  t: number;
  /** Value in data space; null markers are skipped. */
  v: number | null;
}

interface Props {
  points: ScatterPoint[];
  color: string;
  /** Marker radius in px. */
  radius?: number;
  /** Bounds of the line series (used for normalisation — must match WebGL). */
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  zoomState: ZoomState;
  chartWidth: number;
  chartHeight: number;
}

const ScatterOverlayImpl: React.FC<Props> = ({
  points,
  color,
  radius = 3,
  xMin,
  xMax,
  yMin,
  yMax,
  zoomState,
  chartWidth,
  chartHeight,
}) => {
  const visible = useMemo(() => {
    if (chartWidth <= 0 || chartHeight <= 0) return [];
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMin >= xMax) return [];
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMin >= yMax) return [];

    const xRange = xMax - xMin || 1;
    const yRangeRaw = yMax - yMin || 1;
    const yPadding = yRangeRaw * 0.05;
    const yMinPadded = yMin - yPadding;
    const yMaxPadded = yMax + yPadding;
    const yRangePadded = yMaxPadded - yMinPadded;

    const out: Array<{ x: number; y: number }> = [];
    for (const p of points) {
      if (p.v === null || !Number.isFinite(p.v) || !Number.isFinite(p.t)) continue;

      const normalizedX = ((p.t - xMin) / xRange) * 2 - 1;
      const zoomedX = normalizedX * zoomState.scaleX + zoomState.offsetX;
      const screenX = ((zoomedX + 1) / 2) * chartWidth;

      const normalizedY = ((p.v - yMinPadded) / yRangePadded) * 2 - 1;
      const zoomedY = normalizedY * zoomState.scaleY + zoomState.offsetY;
      const screenY = ((1 - zoomedY) / 2) * chartHeight;

      if (
        screenX >= -radius - 1 &&
        screenX <= chartWidth + radius + 1 &&
        screenY >= -radius - 1 &&
        screenY <= chartHeight + radius + 1
      ) {
        out.push({ x: screenX, y: screenY });
      }
    }
    return out;
  }, [points, xMin, xMax, yMin, yMax, zoomState, chartWidth, chartHeight, radius]);

  if (visible.length === 0) return null;

  return (
    <svg
      className="absolute pointer-events-none"
      style={{
        left: CHART_PADDING.left,
        top: CHART_PADDING.top,
        width: chartWidth,
        height: chartHeight,
      }}
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      preserveAspectRatio="none"
    >
      {visible.map((pt, i) => (
        <circle
          key={i}
          cx={pt.x}
          cy={pt.y}
          r={radius}
          fill={color}
          stroke="white"
          strokeWidth={0.5}
        />
      ))}
    </svg>
  );
};

export const ScatterOverlay = React.memo(ScatterOverlayImpl);
